// ============================================================================
// useTaskPolling
// Extracted from SessionTaskDetailModal's poll-while-running loop so the
// inline progress surface on an avatar contribution card can show the same
// live status without opening the modal. Unlike the modal (fine to poll
// indefinitely while explicitly open), this needs a max-duration cutoff —
// an inline surface left running forever on a card nobody is looking at is a
// silent resource leak, not just an idle browser tab.
// ============================================================================

import { useEffect, useRef, useState, useCallback } from 'react';
import { sessionTasksService } from '../../../services/sessionTasksService';
import { TaskExecution } from '../../../types/session-task.types';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export interface TaskPollingState {
  task: TaskExecution | null;
  loading: boolean;
  error: string | null;
  /** True once MAX_POLL_DURATION_MS has passed with the task still running. */
  timedOut: boolean;
}

export function useTaskPolling(
  sessionId: string | undefined,
  workflowId: string | undefined,
  executionId: string | undefined
): TaskPollingState {
  const [task, setTask] = useState<TaskExecution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedAtRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!sessionId || !workflowId || !executionId) return;
    try {
      const execution = await sessionTasksService.getTaskExecution(sessionId, workflowId, executionId);
      setTask(execution);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load task status');
    } finally {
      setLoading(false);
    }
  }, [sessionId, workflowId, executionId]);

  useEffect(() => {
    startedAtRef.current = Date.now();
    setTimedOut(false);
    load();
  }, [load]);

  useEffect(() => {
    if (task?.status !== 'running') return;
    if (timedOut) return;

    const interval = setInterval(() => {
      if (startedAtRef.current && Date.now() - startedAtRef.current > MAX_POLL_DURATION_MS) {
        setTimedOut(true);
        return;
      }
      load();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [task?.status, timedOut, load]);

  return { task, loading, error, timedOut };
}
